// Single source of truth for the Odoo base URL.
// Set REACT_APP_ODOO_URL in .env. It must be reachable from the DEVICE that
// opens the link (a phone scanning the QR), NOT "localhost". There is no
// production fallback on purpose: a misconfigured deploy should fail loudly
// rather than silently call an unreachable internal dev IP.
const ODOO_URL = process.env.REACT_APP_ODOO_URL
  || (process.env.NODE_ENV !== 'production' ? 'https://irrops.rezolv.app' : '');

async function postJson(path, payload) {
  if (!ODOO_URL) {
    throw new Error('REACT_APP_ODOO_URL is not configured for this deployment.');
  }
  const res = await fetch(`${ODOO_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  // Business-logic failures (e.g. "feedback already submitted") come back as
  // a JSON body with success: false on a non-2xx status (409, etc.), so the
  // body must be parsed before deciding how to fail. Only fall back to a
  // bare status message when there's no JSON body at all (e.g. a raw 404/500
  // proxy error page, which would otherwise surface as a confusing "CORS
  // error" with no real cause visible).
  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Server error: ${res.status}`);
  }

  if (!data.success) {
    const error = new Error(data.error || `Server error: ${res.status}`);
    error.alreadySubmitted = Boolean(data.already_submitted);
    error.ticketId = data.ticket_id;
    error.ticketName = data.ticket_name || null;
    throw error;
  }
  return data;
}

export async function verifyQr(partnerId, token) {
  const data = await postJson('/api/partner/verify-qr', {
    partner_id: Number(partnerId),
    token,
  });
  return data.partner;
}

export async function createFeedbackTicket(partner, payload) {
  const { ratings, overallRating, suggestion, ticketNo, pnr } = payload;
  const data = await postJson('/api/passenger/submit', {
    type: 'feedback',
    hotel_id: partner.id,
    passenger_name: ticketNo || '',
    ticket_no: ticketNo,
    pnr,
    overall_rating: overallRating,
    suggestion: suggestion || '',
    ratings,
    team_id: Number(process.env.REACT_APP_HELPDESK_TEAM_ID) || undefined,
  });
  return { id: data.ticket_id, name: data.ticket_name || null };
}

export async function createIncidentTicket(partner, payload) {
  const { issueType, description, ticketNo, pnr } = payload;
  const data = await postJson('/api/passenger/submit', {
    type: 'incident',
    hotel_id: partner.id,
    passenger_name: ticketNo || '',
    ticket_no: ticketNo,
    pnr,
    issue_type: issueType,
    description,
    team_id: Number(process.env.REACT_APP_HELPDESK_TEAM_ID) || undefined,
  });
  return { id: data.ticket_id, name: data.ticket_name || null };
}

export async function attachFileToTicket(ticketId, file) {
  const b64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return postJson('/api/passenger/attach', { ticket_id: ticketId, filename: file.name, data: b64, mimetype: file.type });
}
